export interface PurchasePlannerResponsiveWidths {
  xs: number;
  sm: number;
  md: number;
  lg: number;
}

interface PurchasePlannerAutoFitZoomArgs {
  containerWidth: number;
  tableWidth: number;
  isLaptopViewport: boolean;
}

interface PurchasePlannerVisibleTableMaxHeightArgs {
  containerTop: number;
  viewportBottom: number;
  bottomGap?: number;
  minHeight?: number;
}

interface PurchasePlannerFocusedFieldScrollTopArgs {
  containerScrollTop: number;
  containerTop: number;
  containerHeight: number;
  fieldTop: number;
  fieldBottom: number;
  stickyTopInset?: number;
  margin?: number;
}

export const getPurchasePlannerResponsiveWidths = (
  widths: PurchasePlannerResponsiveWidths,
  isLaptopViewport: boolean,
): Record<string, number> => {
  if (!isLaptopViewport) {
    return { ...widths };
  }

  return {
    xs: Math.max(44, widths.xs - 8),
    sm: Math.max(52, widths.sm - 8),
    md: widths.md - 36,
    lg: widths.lg - 36,
  };
};

export const getPurchasePlannerAutoFitZoom = ({
  containerWidth,
  tableWidth,
  isLaptopViewport,
}: PurchasePlannerAutoFitZoomArgs): number => {
  if (containerWidth <= 0 || tableWidth <= 0) {
    return 1;
  }

  // Touch devices (phones and tablets) lack a fine pointer, so inputs are harder
  // to tap at small zoom levels. Don't auto-shrink — let the table scroll instead.
  if (!isLaptopViewport) {
    return 1;
  }

  const fitRatio = (containerWidth * 0.95) / tableWidth;
  return Math.max(0.78, Math.min(2.0, fitRatio));
};

export const getPurchasePlannerVisibleTableMaxHeight = ({
  containerTop,
  viewportBottom,
  bottomGap = 4,
  minHeight = 220,
}: PurchasePlannerVisibleTableMaxHeightArgs): number => {
  const safeContainerTop = Math.max(containerTop, 0);
  const availableHeight = Math.floor(viewportBottom - safeContainerTop - bottomGap);

  if (!Number.isFinite(availableHeight)) {
    return minHeight;
  }
  // Must not exceed the visible gap (container top → visual viewport bottom).
  // Using Math.max(minHeight, availableHeight) forced e.g. 220px when only ~96px
  // was visible with the software keyboard open, so the table extended under
  // the keyboard and the scrollable region no longer matched what the user saw
  // (only 2–3 rows felt scrollable on iPad).
  if (availableHeight <= 0) {
    return minHeight;
  }
  return Math.min(availableHeight, 1600);
};

export const getPurchasePlannerFocusedFieldScrollTop = ({
  containerScrollTop,
  containerTop,
  containerHeight,
  fieldTop,
  fieldBottom,
  stickyTopInset = 0,
  margin = 12,
}: PurchasePlannerFocusedFieldScrollTopArgs): number | null => {
  const visibleTop = containerTop + stickyTopInset + margin;
  const visibleBottom = containerTop + containerHeight - margin;

  if (fieldTop < visibleTop) {
    return containerScrollTop - (visibleTop - fieldTop);
  }

  if (fieldBottom > visibleBottom) {
    return containerScrollTop + (fieldBottom - visibleBottom);
  }

  return null;
};
